import { Link } from "react-router-dom";
import PageHero from "@/components/feature/PageHero";
import CTASection from "@/components/feature/CTASection";
import { priceRows } from "@/mocks/pricing";

export default function PricingPage() {
  return (
    <>
      <PageHero
        eyebrow="Guide prices"
        title={
          <>
            Clear pricing,<br />
            <em className="not-italic text-accent-400">no surprises.</em>
          </>
        }
        intro="Guide prices for local work in Islington and nearby North London. You'll always be told the likely cost before I start."
        image="https://readdy.ai/api/search-image?query=Neatly%20organised%20electrician%20tool%20bag%20with%20screwdrivers%20and%20test%20meter%20on%20a%20wooden%20floor%20in%20a%20London%20flat%20hallway%2C%20warm%20natural%20light%2C%20realistic%20editorial%20photography%2C%20detailed%20texture%2C%20soft%20shadows&width=1400&height=900&seq=pricing-hero-01&orientation=landscape"
        imageAlt="Electrician's tools laid out in a London flat"
        primaryLabel="Check Availability"
        secondaryLabel="View services"
        secondaryTo="/services"
      />

      <section className="px-4 md:px-8 py-12 sm:py-16 md:py-24 bg-background-50">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-10 items-start">
          <div className="lg:col-span-5">
            <div className="inline-flex items-center gap-2 mb-4 px-3 py-1 rounded-full bg-background-100 border border-background-200 text-[11px] uppercase tracking-[0.18em] text-foreground-700">
              <span className="w-1.5 h-1.5 rounded-full bg-accent-500"></span>
              How pricing works
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-5xl font-heading font-semibold tracking-tight text-foreground-950 leading-[1.05]">
              Straight-forward<br />
              <em className="not-italic text-accent-600">guide prices.</em>
            </h2>
            <p className="mt-4 text-foreground-600 max-w-md">
              Final pricing depends on the job, location, access, parts and
              urgency. I'll always share the likely cost before starting — no
              call-out surprises.
            </p>

            <div className="mt-8 space-y-3">
              {[
                { icon: "ri-price-tag-3-line", title: "Clear quote first", body: "You'll see the likely cost before work begins." },
                { icon: "ri-time-line", title: "Local visits", body: "No inflated travel charges for local Islington jobs." },
                { icon: "ri-tools-line", title: "Parts at cost", body: "Standard parts included where reasonable; larger parts quoted." },
                { icon: "ri-heart-line", title: "Landlord friendly", body: "Batch pricing available for multiple properties." },
              ].map((p) => (
                <div
                  key={p.title}
                  className="flex items-start gap-3 p-4 rounded-xl bg-background-100 border border-background-200"
                >
                  <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-background-50 text-accent-700 shrink-0">
                    <i className={`${p.icon} text-lg`}></i>
                  </span>
                  <div>
                    <p className="text-sm font-semibold text-foreground-950">{p.title}</p>
                    <p className="text-xs text-foreground-600">{p.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="rounded-2xl bg-background-100 border border-background-200 overflow-hidden">
              <div className="hidden sm:flex px-5 md:px-7 py-4 text-[11px] uppercase tracking-[0.16em] text-foreground-500 border-b border-background-200">
                <div className="flex-1">Service</div>
                <div className="w-24 text-right">From</div>
              </div>
              {priceRows.map((r) => (
                <div
                  key={r.service}
                  className="flex items-center gap-3 px-4 sm:px-5 md:px-7 py-4 sm:py-5 border-b border-background-200 last:border-b-0 hover:bg-background-50 transition-colors"
                >
                  <span className="w-10 h-10 flex items-center justify-center rounded-full bg-background-50 text-accent-700 shrink-0">
                    <i className={`${r.icon}`}></i>
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm md:text-base font-semibold text-foreground-950 truncate">
                      {r.service}
                    </p>
                    <p className="text-xs md:text-sm text-foreground-600 line-clamp-2">{r.note}</p>
                  </div>
                  <div className="shrink-0 text-right">
                    <span className="text-lg sm:text-xl md:text-3xl font-heading font-semibold text-accent-700 whitespace-nowrap">
                      {r.from}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-5 flex flex-col sm:flex-row items-start sm:items-center gap-3 p-4 sm:p-5 rounded-xl bg-primary-950 text-background-50">
              <i className="ri-information-line text-2xl text-accent-400 shrink-0"></i>
              <p className="text-sm text-background-100 leading-relaxed">
                Prices are guide prices for local work. You'll always be told the
                likely cost before the job goes ahead.
              </p>
              <Link
                to="/book"
                className="sm:ml-auto shrink-0 group inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-accent-500 text-primary-950 text-sm font-semibold hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                Check Availability
                <i className="ri-arrow-right-up-line"></i>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <CTASection
        eyebrow="Book online"
        title={
          <>
            Get a clear quote,<br />
            <em className="not-italic text-accent-600">without the hassle.</em>
          </>
        }
        subtitle="A short booking form and a couple of photos are all I need to price up your job."
        primaryLabel="Book Appointment"
        secondaryLabel="Send a message"
        secondaryTo="/contact"
      />
    </>
  );
}